import { settings } from "./settings.js";
type Kind =
  | "draw"
  | "slash"
  | "jump"
  | "land"
  | "step"
  | "start"
  | "checkpoint"
  | "finish"
  | "hover"
  | "select"
  | "equip"
  | "context"
  | "case-open"
  | "reveal"
  | "ui"
  | "inspect"
  | "water"
  | "ladder";
export class Sound {
  private context?: AudioContext;
  private paths: Partial<Record<Kind, string>> = {};
  private buffers = new Map<string, Promise<AudioBuffer>>();
  private ambiencePath?: string;
  private ambient?: AudioBufferSourceNode;
  private ambientGain?: GainNode;
  private gameGain?: GainNode;
  private uiGain?: GainNode;
  constructor() {
    void fetch("/assets/manifest.json")
      .then((r) => r.json())
      .then((data) => {
        this.paths = data.sounds ?? {};
        this.ambiencePath = data.ambience ?? undefined;
      })
      .catch(() => {});
  }
  private buffer(path: string) {
    if (!this.buffers.has(path))
      this.buffers.set(
        path,
        fetch(path)
          .then((r) => {
            if (!r.ok) throw new Error("Sound unavailable");
            return r.arrayBuffer();
          })
          .then((b) => this.context!.decodeAudioData(b)),
      );
    return this.buffers.get(path)!;
  }
  applySettings() {
    if (this.ambientGain)
      this.ambientGain.gain.value = settings.master * settings.ambience;
    if (this.gameGain)
      this.gameGain.gain.value = settings.master * settings.game;
    if (this.uiGain) this.uiGain.gain.value = settings.master * settings.ui;
  }
  play(kind: Kind) {
    if (!this.context) {
      this.context = new AudioContext();
      this.gameGain = this.context.createGain();
      this.gameGain.connect(this.context.destination);
      this.uiGain = this.context.createGain();
      this.uiGain.connect(this.context.destination);
    }
    this.applySettings();
    const ctx = this.context;
    if (ctx.state === "suspended") void ctx.resume();
    if (this.ambiencePath && !this.ambient) {
      this.ambient = ctx.createBufferSource();
      this.ambient.loop = true;
      this.ambientGain = ctx.createGain();
      this.ambient.connect(this.ambientGain);
      this.ambientGain.connect(ctx.destination);
      void this.buffer(this.ambiencePath)
        .then((buffer) => {
          this.ambient!.buffer = buffer;
          this.ambient!.start();
        })
        .catch(() => {});
    }
    if (this.ambientGain)
      this.ambientGain.gain.value = settings.master * settings.ambience;
    const isUI = [
      "ui",
      "hover",
      "select",
      "equip",
      "context",
      "case-open",
      "reveal",
    ].includes(kind);
    const bus = isUI ? this.uiGain! : this.gameGain!;
    const volume = isUI ? (kind === "hover" ? 0.13 : 0.38) : 1;
    const path = this.paths[kind];
    if (path) {
      void this.buffer(path)
        .then((buffer) => {
          const source = ctx.createBufferSource(),
            gain = ctx.createGain();
          source.buffer = buffer;
          gain.gain.value = volume;
          source.connect(gain);
          gain.connect(bus);
          source.start();
        })
        .catch(() => {});
      return;
    }
    const osc = ctx.createOscillator(),
      gain = ctx.createGain(),
      now = ctx.currentTime;
    const frequency = {
      draw: 180,
      slash: 150,
      jump: 220,
      land: 75,
      step: 100,
      start: 380,
      checkpoint: 460,
      finish: 660,
      ui: 440,
      hover: 280,
      select: 440,
      equip: 540,
      context: 390,
      "case-open": 190,
      reveal: 660,
      inspect: 350,
      water: 120,
      ladder: 160,
    }[kind];
    osc.type = kind === "finish" ? "sine" : "triangle";
    osc.frequency.setValueAtTime(frequency, now);
    osc.frequency.exponentialRampToValueAtTime(frequency * 0.5, now + 0.12);
    gain.gain.setValueAtTime(volume * 0.09, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.13);
    osc.connect(gain);
    gain.connect(bus);
    osc.start();
    osc.stop(now + 0.14);
  }
}
